import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import { Zap, MapPin, Shield, Users } from 'lucide-react';

const STATS = [
  { 
    icon: Zap, 
    value: 30, 
    suffix: ' dk', 
    label: 'Ort. Müdahale', 
    sub: 'Kocaeli geneli', 
    color: 'rgba(229,51,51,.12)', 
    borderColor: 'rgba(229,51,51,.25)', 
    iconColor: 'var(--red)' 
  },
  { 
    icon: MapPin, 
    value: 12, 
    suffix: '+', 
    label: 'İlçe Kapsama', 
    sub: 'Kesintisiz hizmet', 
    color: 'rgba(74,143,245,.12)', 
    borderColor: 'rgba(74,143,245,.25)', 
    iconColor: 'var(--blue)' 
  },
  { 
    icon: Shield, 
    value: 24, 
    suffix: ' Ay', 
    label: 'Garanti', 
    sub: 'Tüm işlemler', 
    color: 'rgba(16,185,129,.12)', 
    borderColor: 'rgba(16,185,129,.25)', 
    iconColor: 'var(--green)' 
  },
  { 
    icon: Users, 
    value: 247, 
    suffix: '', 
    label: 'Aktif Hizmet', 
    sub: 'Her gün, her saat', 
    color: 'rgba(255,175,50,.1)', 
    borderColor: 'rgba(255,175,50,.25)', 
    iconColor: '#ff9500' 
  },
];

export default function Stats() {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);

  useEffect(() => {
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVis(true); },
      { threshold: .25 }
    );
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, []);

  return (
    <section 
      ref={ref} 
      data-testid="stats-section"
      style={{
        background: 'var(--surface)',
        borderTop: '1px solid var(--border)',
        borderBottom: '1px solid var(--border)',
        padding: '0',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Ambient glow */}
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '20%',
        transform: 'translate(-50%, -50%)',
        width: 600,
        height: 400,
        background: 'radial-gradient(ellipse, rgba(74,143,245,.06) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      <div className="wrap">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
          }}
        >
          {STATS.map(({ icon: Icon, value, suffix, label, sub, color, borderColor, iconColor }, i) => (
            <motion.div
              key={label}
              data-testid={`stat-item-${i}`}
              initial={{ opacity: 0, y: 20 }}
              animate={vis ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: .6, delay: i * .1, ease: [.25, .46, .45, .94] }}
              style={{
                padding: '3rem 1.5rem',
                borderRight: i < 3 ? '1px solid var(--border)' : 'none',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                gap: '1rem',
                position: 'relative',
                transition: 'background .3s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(13,14,32,.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              {/* Icon */}
              <div
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: 14,
                  background: color,
                  border: `1px solid ${borderColor}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: iconColor,
                  transition: 'all .3s',
                }}
              >
                <Icon size={22} strokeWidth={1.75} />
              </div>

              {/* Number */}
              <div>
                <div
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: 800,
                    fontSize: 'clamp(2.25rem, 4vw, 3rem)',
                    color: 'var(--text)',
                    lineHeight: 1,
                    letterSpacing: '-.04em',
                    marginBottom: '.5rem',
                  }}
                >
                  {vis ? <CountUp end={value} duration={2.2} separator="" /> : '0'}
                  <span style={{ color: iconColor }}>{suffix}</span>
                </div>

                {/* Label */}
                <div
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: 600,
                    fontSize: '.975rem',
                    color: 'var(--text)',
                    marginBottom: '.25rem',
                  }}
                >
                  {label}
                </div>

                {/* Sub */}
                <div
                  style={{
                    fontSize: '.775rem',
                    color: 'var(--text2)',
                  }}
                >
                  {sub}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <style>{`
        @media(max-width: 768px) {
          [data-testid="stats-section"] > div > div {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          [data-testid="stats-section"] > div > div > div:nth-child(2),
          [data-testid="stats-section"] > div > div > div:nth-child(4) {
            border-right: none !important;
          }
          [data-testid="stats-section"] > div > div > div:nth-child(1),
          [data-testid="stats-section"] > div > div > div:nth-child(2) {
            border-bottom: 1px solid var(--border);
          }
          [data-testid="stats-section"] > div > div > div:nth-child(3) {
            border-right: 1px solid var(--border) !important;
          }
        }
      `}</style>
    </section>
  );
}
